import { Mutation, Action, VuexModule, getModule, Module } from 'vuex-module-decorators'
import store from '@/store/store'
import { PageList, PageMap, SitePageMap, TargetType } from '../../modules/class/PageList'
import { Country, CountryList } from '../../modules/class/CountryList'
import { SectionItem, SectionList, SectionMap } from '@/modules/class/SectionList'
import { wait } from '@/modules/util/Wait'

const WAIT_TIME = 10

@Module({ dynamic: true, store, name: 'download-state', namespaced: true })
class DownloadState extends VuexModule {
  // -------------------------------------------------------------------------------------------------------------------
  // state
  // ⚡Stateでコンポーネント間における共通の状態を管理します。Stateはコンポーネントから利用されます。⚡

  private _loaded: Set<string> = new Set
  get loaded() {
    return this._loaded
  }

  private _loading: Set<string> = new Set

  /**
   * ページのマップ
   */
  private _pageMap: PageMap = new Map()
  get pageMap() {
    return this._pageMap
  }

  /**
   * 製品に紐づくページのマップ
   */
  private _productPageMap: SitePageMap = new Map()
  get productPageMap() {
    return this._productPageMap
  }

  /**
   * 分野に紐づくページのマップ
   */
  private _industryPageMap: SitePageMap = new Map()
  get industryPageMap() {
    return this._industryPageMap
  }

  /**
   * 分野カテゴリに紐づくページのマップ
   */
  private _industryCategoryPageMap: SitePageMap = new Map()
  get industryCategoryPageMap() {
    return this._industryCategoryPageMap
  }

  /**
   * 事例カテゴリに紐づくページのマップ
   */
  private _casePageMap: SitePageMap = new Map()
  get casePageMap() {
    return this._casePageMap
  }

  /**
   * セクションのマップ
   */
  private _sectionMap: SectionMap = new Map()
  get sectionMap() {
    return this._sectionMap
  }

  /**
   * セクションのリスト
   */
  private _sectionList: SectionItem[] = []
  get sectionList() {
    return this._sectionList
  }

  /**
   * 国リスト
   */
  private _countryList: Country[] = []
  get countryList() {
    return this._countryList
  }

  /**
   * 表示しているテーブルのアンカーリンク
   */
  private _anchorList: string[] = []
  get anchorList() {
    return this._anchorList
  }

  // ------------------------------------------------------------------------------------------------
  // mutations
  // ⚡MutationsでStateの値を更新します。⚡

  @Mutation
  public addLoaded(s: string) {
    this._loaded.add(s)
  }

  @Mutation
  public setPageMap(m: PageMap) {
    this._pageMap = m
  }

  @Mutation
  public setProductPageMap(m: SitePageMap) {
    this._productPageMap = m
  }

  @Mutation
  public setIndustryPageMap(m: SitePageMap) {
    this._industryPageMap = m
  }

  @Mutation
  public setIndustryCategoryPageMap(m: SitePageMap) {
    this._industryCategoryPageMap = m
  }

  @Mutation
  public setCasePageMap(m: SitePageMap) {
    this._casePageMap = m
  }

  @Mutation
  public setSectionMap(m: SectionMap) {
    this._sectionMap = m
  }

  @Mutation
  public setSectionList(s: SectionItem[]) {
    this._sectionList = s
  }

  @Mutation
  public setCountryList(c: Country[]) {
    this._countryList = c
  }

  @Mutation
  public setAnchor(a: string) {
    this._anchorList.push(a)
  }

  // ------------------------------------------------------------------------------------------------
  // action
  // ⚡ActionでAPI等の外部通信を行う処理や、コンポーネントから呼び出された時の処理を行い、Mutationを利用します。⚡

  /**
   * ページリスト取得
   */
  @Action({ rawError: true })
  public async fetchPageMap() {

    // 既に取得済みの場合、終了
    if (this._pageMap.size) {
      return
    }

    // 誰かがロード中の場合、終了
    const type = 'page'
    if (this._loaded.has(type)) {
      return
    }
    this.addLoaded(type)

    // セクション情報を取得
    if (this._sectionMap.size === 0) {
      await this.fetchSectionMap()
    }

    // ページ情報を取得
    const pageListObject = new PageList(this._sectionMap)
    const map = await pageListObject.getPageListAll()

    // ロード完了
    this.setPageMap(map)
  }

  /**
   * 製品に紐づくページリスト取得
   */
  @Action({ rawError: true })
  public async fetchProductPageMap() {
    const type: TargetType = 'product'

    // 既に取得済みの場合、終了
    if (this._productPageMap.size) {
      return
    }

    // 既にロード中なら完了まで待つ
    if (this._loading.has(type)) {
      const loading = async () => {
        while (this._loading.has(type)) {
          await wait(WAIT_TIME);
        }
      }
      await loading();
      return
    }
    this._loading.add(type)

    // 一覧を取得
    const map = await this.fetchTargetPageMap(type)
    this.setProductPageMap(map)

    // ロード完了
    this._loading.delete(type)
  }

  /**
   * 分野に紐づくページリスト取得
   */
  @Action({ rawError: true })
  public async fetchIndustryPageMap() {
    const type: TargetType = 'industry'

    // 既に取得済みの場合、終了
    if (this._industryPageMap.size) {
      return
    }

    // 既にロード中なら完了まで待つ
    if (this._loading.has(type)) {
      const loading = async () => {
        while (this._loading.has(type)) {
          await wait(WAIT_TIME);
        }
      }
      await loading();
      return
    }
    this._loading.add(type)

    // 一覧を取得
    const map = await this.fetchTargetPageMap(type)
    this.setIndustryPageMap(map)

    // ロード完了
    this._loading.delete(type)
  }

  /**
   * 分野カテゴリに紐づくページリスト取得
   */
  @Action({ rawError: true })
  public async fetchIndustryCategoryPageMap() {
    const type: TargetType = 'industry_category'

    // 既に取得済みの場合、終了
    if (this._industryCategoryPageMap.size) {
      return
    }

    // 既にロード中なら完了まで待つ
    if (this._loading.has(type)) {
      const loading = async () => {
        while (this._loading.has(type)) {
          await wait(WAIT_TIME);
        }
      }
      await loading();
      return
    }
    this._loading.add(type)

    // 一覧を取得
    const map = await this.fetchTargetPageMap(type)
    this.setIndustryCategoryPageMap(map)

    // ロード完了
    this._loading.delete(type)
  }

  /**
   * 事例に紐づくページリスト取得
   */
  @Action({ rawError: true })
  public async fetchCasePageMap() {
    const type: TargetType = 'case'

    // 既に取得済みの場合、終了
    if (this._casePageMap.size) {
      return
    }

    // 既にロード中なら完了まで待つ
    if (this._loading.has(type)) {
      const loading = async () => {
        while (this._loading.has(type)) {
          await wait(WAIT_TIME);
        }
      }
      await loading();
      return
    }
    this._loading.add(type)

    // 一覧を取得
    const map = await this.fetchTargetPageMap(type)
    this.setCasePageMap(map)

    // ロード完了
    this._loading.delete(type)
  }

  /**
   * セクションマップを取得
   */
  @Action({ rawError: true })
  public async fetchSectionMap() {
    await this.fetchSectionList()
  }

  /**
   * セクションリストを取得
   */
  @Action({ rawError: true })
  public async fetchSectionList() {
    const type = 'section'

    // 既に取得済みの場合、終了
    if (this._sectionList.length > 0) {
      return
    }

    // 既にロード中なら完了まで待つ
    if (this._loading.has(type)) {
      const loading = async () => {
        while (this._loading.has(type)) {
          await wait(WAIT_TIME);
        }
      }
      await loading();
      return
    }
    this._loading.add(type)

    // 一覧を取得
    const sectionListObject = new SectionList()
    const list = await sectionListObject.getSectionList()
    this.setSectionList(list)

    const map = await sectionListObject.getSectionMap()
    this.setSectionMap(map)

    this._loading.delete(type)
  }

  /**
   * 国リスト取得
   */
  @Action({ rawError: true })
  public async fetchCountryList() {
    const countryList = await CountryList.getCountryList()
    this.setCountryList(countryList)
  }

  // ------------------------------------------------------------------------------------------------
  // private

  /**
   * 指定したデータに紐づくページリスト取得
   */
  @Action({ rawError: true })
  public async fetchTargetPageMap(targetType: TargetType): Promise<SitePageMap> {

    // セクション情報を取得
    if (this._sectionMap.size === 0) {
      await this.fetchSectionMap()
    }

    // ページ情報を取得
    const pageListObject = new PageList(this._sectionMap)
    const map = await pageListObject.getTargetPageMap(targetType)

    return map
  }

}

export const downloadState = getModule(DownloadState)
